import { has, isPlainObject } from 'lodash';
import { getDefaultValue, Property } from '../../../views/CustomProperties/Properties/types';
import { PropertySet } from '../../../views/CustomProperties/PropertySets/types';
import { PropertySetsValues, PropertySetValues, PropertyValue } from './types';

export interface PropertyDefinitions {
  propertiesById: Map<number, Property>;
  propertySetsById: Map<number, PropertySet>;
}

const asValues = (value: unknown): PropertySetValues =>
  isPlainObject(value) ? (value as PropertySetValues) : {};

/**
 * One set's values, completed from its definition: every property it doesn't have yet gets its
 * default, nested sets and property-set list items are completed the same way, and keys the set
 * no longer defines are dropped. Values already saved are kept as they are.
 */
const initializeSetValues = (
  setDef: PropertySet,
  current: unknown,
  definitions: PropertyDefinitions,
  ancestors: string[]
): PropertySetValues => {
  const values = asValues(current);
  // A set that contains itself further up the chain is rendered as an error, not edited.
  if (ancestors.includes(setDef.replacementKey)) return values;
  const chain = [...ancestors, setDef.replacementKey];
  const initialized: PropertySetValues = {};

  setDef.setProperties.properties.forEach((id) => {
    const property = definitions.propertiesById.get(id);
    if (!property) return;
    initialized[property.replacementKey] = has(values, [property.replacementKey, 'value'])
      ? (values[property.replacementKey] as PropertyValue)
      : ({
          value: property.additionalConfiguration?.defaultValue || getDefaultValue(property.type),
          overrideByURL: false,
        } as PropertyValue);
  });

  setDef.setProperties.propertySets.forEach((id) => {
    const nestedSet = definitions.propertySetsById.get(id);
    if (!nestedSet) return;
    initialized[nestedSet.replacementKey] = initializeSetValues(
      nestedSet,
      values[nestedSet.replacementKey],
      definitions,
      chain
    );
  });

  setDef.setProperties.listProperties?.forEach((listProperty) => {
    if (!has(values, listProperty.replacementKey)) return;
    const items = values[listProperty.replacementKey];
    const elementSet =
      listProperty.listType === 'propertySet' && listProperty.elementId !== undefined
        ? definitions.propertySetsById.get(listProperty.elementId)
        : undefined;
    // Each item of a property-set list holds that set's values under the set's key.
    initialized[listProperty.replacementKey] =
      elementSet && Array.isArray(items)
        ? (items as PropertySetValues[]).map((item) => ({
            ...asValues(item),
            [elementSet.replacementKey]: initializeSetValues(
              elementSet,
              asValues(item)[elementSet.replacementKey],
              definitions,
              chain
            ),
          }))
        : (items as PropertyValue);
  });

  return initialized;
};

/**
 * The values to save for an entity's property sets: each attached set completed from its
 * definition. This runs over the whole tree in one pass so a set's defaults are saved as soon as
 * it is attached — not only after someone selects it — and in a single update, so nothing else
 * can overwrite them from an older copy of the values. Keys of sets that aren't attached are left
 * alone; removing a set removes its values separately.
 */
export const initializePropertySetsValues = (
  propertySets: PropertySet[],
  propertySetsValues: PropertySetsValues | null | undefined,
  definitions: PropertyDefinitions
): PropertySetsValues => {
  const initialized: PropertySetsValues = { ...(propertySetsValues ?? {}) };
  propertySets.forEach((setDef) => {
    initialized[setDef.replacementKey] = initializeSetValues(
      setDef,
      initialized[setDef.replacementKey],
      definitions,
      []
    );
  });
  return initialized;
};
